import { useEffect, useRef, useState } from 'react';
import { cardSrc } from './resultParsers';

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img';
const ITEM_W = 34.386;
const PER_VIEW = 8; // most cards shown; fewer when the column is narrower

const SUIT_NAME = { SS: 'Spade', HH: 'Heart', CC: 'Club', DD: 'Diamond' };

function rankOf(code) {
  return String(code).slice(0, -2).toUpperCase();
}
function rankVal(r) {
  if (r === 'A') return 1;
  if (r === 'J') return 11;
  if (r === 'Q') return 12;
  if (r === 'K') return 13;
  return parseInt(r, 10) || 0;
}

// Owl result slider. An arrow moves
// the visible set, not a fixed row of 8 (that read as skipping and felt too fast), and a
// finger drag follows the finger, then settles on the nearest whole card on
// release. The visible strip is clipped to whole cards (up to PER_VIEW), so no
// sliver of the next card shows (user 2026-10-19). The drag writes the transform straight to the stage (no React
// render per finger move) so it stays smooth; release eases out to a card.
// Arrow step = the cards on screen: 1 2 3 4 → 5 6 7 8 (user 2026-10-19). The old
// fixed 8 skipped cards when only 4 fit; a 1- or 3-card step was not what was asked.
// Slower glide, like the reference (user 2026-10-19: ours scrolled too fast).
const SLIDE_MS = 800;
const EASE = 'ease-in-out';
// A finger move longer than this (px) decides the drag axis.
const DRAG_MIN_PX = 6;

function ResultOwl({ id, cards }) {
  const boxRef = useRef(null);
  const stageRef = useRef(null);
  const touch = useRef(null);
  const [offset, setOffset] = useState(0);
  const [perView, setPerView] = useState(PER_VIEW);

  // Measured on the carousel box (its column), not on the clipped stage-outer.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setPerView(Math.max(1, Math.min(PER_VIEW, Math.floor(el.clientWidth / ITEM_W))));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Last stop keeps the strip full (the last perView cards), not 1-2 cards and a gap (user 2026-10-19).
  const maxOffset = Math.max(0, cards.length - perView);
  const cur = Math.min(offset, maxOffset);
  const atStart = cur <= 0;
  const atEnd = cur >= maxOffset;
  const clamp = (n) => Math.min(maxOffset, Math.max(0, n));
  const prev = () => setOffset((o) => clamp(Math.min(o, maxOffset) - perView));
  const next = () => setOffset((o) => clamp(Math.min(o, maxOffset) + perView));

  // Only a mostly horizontal move drags; touch-action pan-y leaves vertical
  // swipes to the modal's own scroll.
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, axis: null };
  };
  const onTouchMove = (e) => {
    const st = touch.current;
    const stage = stageRef.current;
    if (!st || !stage) return;
    const t = e.touches[0];
    const dx = t.clientX - st.x;
    const dy = t.clientY - st.y;
    if (!st.axis) {
      if (Math.abs(dx) < DRAG_MIN_PX && Math.abs(dy) < DRAG_MIN_PX) return;
      st.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (st.axis === 'x') stage.style.transition = 'none';
    }
    if (st.axis !== 'x') return;
    st.dx = dx;
    // Past either end the strip only gives a little (rubber band).
    let x = -(cur * ITEM_W) + dx;
    const min = -(maxOffset * ITEM_W);
    if (x > 0) x /= 3;
    else if (x < min) x = min + (x - min) / 3;
    stage.style.transform = `translate3d(${x}px, 0px, 0px)`;
  };
  const onTouchEnd = () => {
    const st = touch.current;
    touch.current = null;
    const stage = stageRef.current;
    if (!st || st.axis !== 'x' || !stage) return;
    const to = clamp(cur + Math.round(-(st.dx || 0) / ITEM_W));
    stage.style.transition = `transform ${SLIDE_MS}ms ${EASE}`;
    stage.style.transform = `translate3d(${-(to * ITEM_W)}px, 0px, 0px)`;
    setOffset(to);
  };

  const translateX = -(cur * ITEM_W);
  const stageWidth = cards.length * ITEM_W;

  return (
    <div ref={boxRef} id={id} className="ab-slider owl-carousel owl-theme owl-loaded owl-drag">
      <div
        className="owl-stage-outer"
        style={{ width: `${perView * ITEM_W}px`, overflow: 'hidden', touchAction: 'pan-y' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div ref={stageRef} className="owl-stage" style={{ transform: `translate3d(${translateX}px, 0px, 0px)`, transition: `transform ${SLIDE_MS}ms ${EASE}`, willChange: 'transform', width: `${stageWidth}px` }}>
          {cards.map((code, i) => {
            const active = i >= cur && i < cur + perView;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={{ width: `${ITEM_W}px` }}>
                <div className="casino-result-cards-item item"><img src={cardSrc(code)} alt={code} draggable={false} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`owl-nav${cards.length <= perView ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}><span aria-label="Previous">‹</span></button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}><span aria-label="Next">›</span></button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
}

export default function ABJResult({ detail }) {
  const allCards = (detail?.card || '').split(',').map((c) => c.trim()).filter(Boolean);
  const trumpCard = allCards[0] && allCards[0] !== '1' ? allCards[0] : null;
  const dealt = allCards.slice(1).filter((c) => c !== '1' && c !== '0');
  const aCards = dealt.filter((_, i) => i % 2 === 1);
  const bCards = dealt.filter((_, i) => i % 2 === 0);

  const aFirst = aCards[0];
  const aRest = aCards.slice(1);
  const bFirst = bCards[0];
  const bRest = bCards.slice(1);

  const winnat = detail?.winnat || '';
  const win = String(detail?.win ?? '');
  const isAWinner = winnat.toLowerCase() === 'andar' || win === '1';
  const isBWinner = winnat.toLowerCase() === 'bahar' || win === '2';
  const winnerText = isAWinner ? 'Andar' : isBWinner ? 'Bahar' : (winnat || '');

  // desc rdesc se seedhe: "<Winner>#<Suit>#<Odd/Even>#<Joker>" (e.g. "Bahar#Heart#Even#Q");
  // rdesc na ho to trump card se derive
  const rparts = String(detail?.rdesc || '').split('#');
  const suit = rparts[1] || (trumpCard ? (SUIT_NAME[String(trumpCard).slice(-2).toUpperCase()] || '') : '');
  const oddEven = rparts[2] || (trumpCard ? (rankVal(rankOf(trumpCard)) % 2 === 1 ? 'Odd' : 'Even') : '');
  const joker = rparts[3] || (trumpCard ? rankOf(trumpCard) : '');

  const winnerIcon = (
    <div className="casino-result-cards-item">
      <img src={`${CDN}/winner.png`} className="winner-icon" alt="winner" />
    </div>
  );

  return (
    <div className="row row5 ab2-result">
      <div className="col-12 col-lg-8">
        <div className="d-flex">
          <div className="col-1 abj-title">
            <div className="row row5"><div className="col-12"><b>A</b></div></div>
            <div className="row row5"><div className="col-12"><b>B</b></div></div>
          </div>
          <div className="abj-common-card">
            {trumpCard && (
              <div className="casino-result-cards-item"><img src={cardSrc(trumpCard)} alt={trumpCard} /></div>
            )}
          </div>
          <div className="col-9">
            {/* Andar row */}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-2">
                  {aFirst && <div className="casino-result-cards-item"><img src={cardSrc(aFirst)} alt={aFirst} /></div>}
                </div>
                <div className="col-8"><ResultOwl id="andarSlider" cards={aRest} /></div>
                <div className="col-2">{isAWinner ? winnerIcon : null}</div>
              </div>
            </div>
            {/* Bahar row */}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-2">
                  {bFirst && <div className="casino-result-cards-item"><img src={cardSrc(bFirst)} alt={bFirst} /></div>}
                </div>
                <div className="col-8"><ResultOwl id="baharSlider" cards={bRest} /></div>
                <div className="col-2">{isBWinner ? winnerIcon : null}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-4">
        <div className="casino-result-desc">
          <div className="casino-result-desc-item">
            <div>Winner <span className="float-right">:</span></div>
            <div>{winnerText}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Suit<span className="float-right">:</span></div>
            <div>{suit}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Odd/Even<span className="float-right">:</span></div>
            <div>{oddEven}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Joker<span className="float-right">:</span></div>
            <div>{joker}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
